import { h } from 'vue';
import {
  NegarMarketIcon,
  NegarInvestmentDocumentIcon,
  NegarCourtLegalLawDecisionIcon,
  NegarHomeHouseBigIcon,
  NegarStockIcon,
  NegarIncomeIcon,
  NegarCoinsIcon,
  NegarBankIcon,
  NegarBuildingIcon,
  NegarCarIcon,
  NegarCementIcon,
  NegarChemicalIcon,
  NegarFoodIndustryIcon,
  NegarComputerIcon,
  NegarExtractionMetalsIcon,
  NegarHoldingIcon,
  NegarAgricultureIcon,
  NegarInsuranceIcon,
  NegarInvestmentIcon,
  NegarMachineIcon,
  NegarMetalsIcon,
  NegarMedicinalIcon,
  NegarNonMetallicMineralIcon,
  NegarOilProductsIcon,
  NegarRubberIcon,
  NegarSugarIcon,
  NegarTileIcon,
  NegarTransportIcon,
  NegarOptionTradeIcon,
  NegarLeverageIcon,
  NegarCandelChartIcon,
  NegarPresentationLightIcon,
  NegarIndustryIcon,
  NegarOptionChainIcon,
} from '@negar/icons';
import { DeepPartial, LineWidth } from 'lightweight-charts';
import {
  INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  TRADING_STATE_CODES,
} from '@core/symbol';
import { i18n } from '@/plugins/i18n';

import {
  IndustryGroupsTable,
  SymbolDetail,
  SymbolTechnicalChart,
} from '@/modules/symbol/index';
import { OptionChainTable } from '@/modules/option';

const { t } = i18n.global;

const SYMBOLS_CATEGORY_LABEL = {
  share: t('search.symbol_groups.share'),
  shareRight: t('search.symbol_groups.share_right'),
  // Exchange Traded Fund
  ETF: t('search.symbol_groups.exchange_traded_fund'),
  commodityDepositCertificate: t(
    'search.symbol_groups.commodity_deposit_certificate'
  ),
  mortgageBond: t('search.symbol_groups.mortgage_bond'),
  treasuryBond: t('search.symbol_groups.treasury_bond'),
  debtBond: t('search.symbol_groups.debt_bond'),
  option: t('search.symbol_groups.optionTrading'),
  others: t('search.symbol_groups.others'),
};

const SALAM_ISIN = 'IRT1FASA0001';

const GROUPING_INDUSTRY_SYMBOLS_SERVICE = {
  1: {
    icon: h(NegarAgricultureIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  13: {
    icon: h(NegarExtractionMetalsIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  23: {
    icon: h(NegarOilProductsIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  25: {
    icon: h(NegarRubberIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  27: {
    icon: h(NegarMetalsIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  29: {
    icon: h(NegarMachineIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  34: {
    icon: h(NegarCarIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  38: {
    icon: h(NegarSugarIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  39: {
    icon: h(NegarHoldingIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  42: {
    icon: h(NegarFoodIndustryIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  43: {
    icon: h(NegarMedicinalIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  44: {
    icon: h(NegarChemicalIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  49: {
    icon: h(NegarTileIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  53: {
    icon: h(NegarCementIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  54: {
    icon: h(NegarNonMetallicMineralIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  56: {
    icon: h(NegarInvestmentIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  57: {
    icon: h(NegarBankIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  60: {
    icon: h(NegarTransportIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  66: {
    icon: h(NegarInsuranceIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  70: {
    icon: h(NegarBuildingIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
  72: {
    icon: h(NegarComputerIcon),
    types: INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  },
};
const GROUPING_INDUSTRY_SYMBOLS = {
  functionalCategories: {
    name: t('search.functionalCategories'),
    symbols: [
      {
        name: t('search.baseMarket.yellow'),
        icon: h(NegarMarketIcon, { class: 'text-warning-secondary' }),
        filters: {
          types: [309],
          instrumentGroupIds: ['P1', 'P2', 'P3', 'P4'],
        },
      },
      {
        name: t('search.baseMarket.orange'),
        icon: h(NegarMarketIcon, { class: 'text-warning-primary' }),
        filters: {
          types: [309],
          instrumentGroupIds: ['C1', 'C2', 'C3', 'C4'],
        },
      },
      {
        name: t('search.baseMarket.red'),
        icon: h(NegarMarketIcon, { class: 'text-critical-primary' }),
        filters: {
          types: [309],
          instrumentGroupIds: ['L1', 'L2', 'L3', 'L4'],
        },
      },

      {
        name: t('search.housingFacilities'),
        icon: h(NegarHomeHouseBigIcon),
        filters: {
          sectorCodes: 59,
        },
      },

      {
        name: t('search.symbol_groups.treasury_bond'),
        icon: h(NegarCourtLegalLawDecisionIcon),
        filters: {
          subSectorCodes: [6940, 6944],
        },
      },
      {
        name: t('search.symbol_groups.debt_bond'),
        icon: h(NegarInvestmentDocumentIcon),
        filters: {
          subSectorCodes: [
            6910, 6911, 6912, 6913, 6914, 6920, 6921, 6922, 6923, 6924, 6925,
            6930, 6031, 6941, 6942, 6943, 6945, 6946, 6947, 6948, 6949,
          ],
        },
      },
    ],
  },
  tradableFunds: {
    name: t('search.symbol_groups.exchange_traded_fund'),
    symbols: [
      {
        name: t('search.commodityFund'),
        group: 1,
        icon: h(NegarCoinsIcon),
        filters: {
          types: [380, 315],
          subSectorCodes: [6822, 6823, 6824, 6820],
        },
      },
      {
        name: t('search.stockFund'),
        group: 1,
        icon: h(NegarStockIcon),
        filters: {
          types: 305,
          subSectorCodes: 6811,
        },
      },
      {
        name: t('search.leverageFund'),
        group: 1,
        icon: h(NegarLeverageIcon),
        filters: { types: 305, subSectorCodes: 6821 },
      },
      {
        name: t('search.fixedIncomeFund'),
        group: 1,
        icon: h(NegarIncomeIcon),
        filters: {
          subSectorCodes: 6812,
        },
      },
    ],
  },
};

const SYMBOL_LINE_CHART_FILTERS = {
  oneDay: {
    id: 'oneDay',
    dateType: 1,
    label: t('misc.today'),
    interval: 2,
    value: 0,
    dateFormat: 'longDate',
  },
  oneWeek: {
    id: 'oneWeek',
    dateType: 2,
    label: t('symbol.charts.oneWeek'),
    interval: 3,
    value: 1,
    dateFormat: 'longDate',
  },
  oneMonth: {
    id: 'oneMonth',
    dateType: 3,
    label: t('symbol.charts.oneMonth'),
    interval: 6,
    value: 2,
    dateFormat: 'shortDate',
  },
  threeMonth: {
    id: 'threeMonth',
    dateType: 4,
    label: t('symbol.charts.threeMonth'),
    interval: 6,
    value: 3,
    dateFormat: 'shortDate',
  },
  sixMonth: {
    id: 'sixMonth',
    dateType: 5,
    label: t('symbol.charts.sixMonth'),
    interval: 6,
    value: 4,
    dateFormat: 'shortDate',
  },
  all: {
    id: 'all',
    dateType: 6,
    label: t('symbol.charts.oneYear'),
    interval: 6,
    value: 5,
    dateFormat: 'shortDate',
  },
};

const CHART_PRICE_LINE_COLOR_PALETTE = {
  auto: '#B2B2B2',
  light: '#B2B2B2',
  dark: '#808080',
};

const CHART_SERIES_COLOR_PALETTE = {
  auto: {
    GREEN: '#167B47',
    RED: '#D82A39',
    GRAY: '#808080',
  },
  light: {
    GREEN: '#167B47',
    RED: '#D82A39',
    GRAY: '#8C8C8C',
  },
  dark: {
    GREEN: '#4CDC91',
    RED: '#EA3948',
    GRAY: '#404040',
  },
};

const LINE_CHART_OPTIONS_LAYOUT_BACKGROUND_COLOR_PALETTE = {
  auto: '#FFF',
  light: '#FFF',
  dark: '#0D0D0D',
};

const LINE_CHART_OPTIONS = {
  height: 160,
  color: '#808080',
  autoSize: false,
  lineWidth: 2 as DeepPartial<LineWidth>,
  priceLineVisible: false,
  lastValueVisible: false,
  timeScale: {
    visible: false,
  },
  crosshair: {
    vertLine: {
      visible: true,
      style: 1,
      width: 1 as DeepPartial<LineWidth>,
      color: '#CCCCCC',
      labelVisible: false,
    },
    horzLine: {
      visible: false,
      labelVisible: false,
    },
  },
  grid: {
    vertLines: {
      visible: false,
    },
    horzLines: {
      visible: false,
    },
  },
  rightPriceScale: {
    visible: false,
  },
  leftPriceScale: {
    visible: false,
  },
  localization: {
    locale: 'fa',
  },
  handleScroll: false,
  handleScale: {
    axisPressedMouseMove: false,
    axisDoubleClickReset: false,
    mouseWheel: false,
    pinch: true,
  },
  trackingMode: {
    exitMode: 0,
  },
};

const TRADING_STATE_INFO = {
  [TRADING_STATE_CODES.closed]: {
    title: t('orders.twentyFourHour'),
    description: t('symbol.state.closedDescription'),
    bgColor: 'bg-purple-primary-disable',
  },
  [TRADING_STATE_CODES.preOpening]: {
    title: t('misc.pre-opening'),
    description: t('symbol.state.preOpeningDescription'),
    bgColor: 'bg-warning-secondary',
  },
  [TRADING_STATE_CODES.open]: {
    title: t('misc.open'),
    description: t('symbol.state.openDescription'),
    bgColor: 'bg-brand-secondary',
  },
  [TRADING_STATE_CODES.forbidden]: {
    title: t('orders.twentyFourHour'),
    description: t('symbol.state.forbiddenDescription'),
    bgColor: 'bg-purple-primary-disable',
  },
} as const;

const SYNBOL_SUMMARY_TOGGLE_BUTTON_ID = {
  symbolDetail: 0,
  symbolChart: 1,
};

const SYMBOL_COMPONENTS_TABS = {
  chart: {
    id: 0,
    label: t('symbol.technical'),
    component: SymbolTechnicalChart,
    icon: NegarCandelChartIcon,
    badge: '',
    isOptionٍElement: false,
    gidComponent: 'chartTabButton',
  },
  detail: {
    id: 1,
    label: t('symbol.symbol_info'),
    component: SymbolDetail,
    icon: NegarPresentationLightIcon,
    badge: '',
    isOptionٍElement: false,
    gidComponent: 'detailTabButton',
  },
  industryGroups: {
    id: 2,
    label: t('symbol.industryGroups'),
    component: IndustryGroupsTable,
    icon: NegarIndustryIcon,
    badge: '',
    isOptionٍElement: false,
    gidComponent: 'industryTabButton',
  },
  optionChain: {
    id: 3,
    label: t('symbol.optionChain'),
    component: OptionChainTable,
    icon: NegarOptionChainIcon,
    badge: t('misc.new'),
    isOptionٍElement: true,
    gidComponent: 'optionChainTabButton',
  },
};

export {
  SYMBOLS_CATEGORY_LABEL,
  SALAM_ISIN,
  GROUPING_INDUSTRY_SYMBOLS_SERVICE,
  GROUPING_INDUSTRY_SYMBOLS,
  SYMBOL_LINE_CHART_FILTERS,
  CHART_SERIES_COLOR_PALETTE,
  LINE_CHART_OPTIONS,
  LINE_CHART_OPTIONS_LAYOUT_BACKGROUND_COLOR_PALETTE,
  CHART_PRICE_LINE_COLOR_PALETTE,
  TRADING_STATE_INFO,
  SYNBOL_SUMMARY_TOGGLE_BUTTON_ID,
  SYMBOL_COMPONENTS_TABS,
};
